import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import {
  ArrowLeft,
  Banknote,
  CalendarClock,
  Check,
  CircleDollarSign,
  Link2,
  Package,
  ScanLine,
  ShieldCheck,
  X,
} from 'lucide-react'
import { Link, useParams } from 'react-router-dom'

import { PageHeader, PageHeaderToolbar } from '@/components/app/PageHeader'
import { SortableTableHead, type SortDirection } from '@/components/app/SortableTableHead'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { KpiCard } from '@/features/dashboard/components/KpiCard'
import { useAuthSession } from '@/features/auth/session'
import { useAppBreadcrumbTrail } from '@/layouts/AppShell'
import { ApiError } from '@/lib/api'
import { cn } from '@/lib/utils'
import { getAppLocale } from '@/lib/locale'

import { fetchTransaction } from '../api'

type SyncSortKey = 'updatedAt'
type AuditSortKey = 'occurredAt'
type StepState = 'completed' | 'active' | 'pending' | 'error'

interface WorkflowStep {
  key: string
  label: string
  icon: React.ComponentType<{ className?: string }>
  date: string | null
  state: StepState
}

function formatDate(value: string | null, withTime = false, t?: (key: string) => string) {
  if (!value) {
    return t ? t('common.notAvailable') : 'Unavailable'
  }

  return new Date(value).toLocaleString(getAppLocale(), {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    ...(withTime
      ? {
          hour: '2-digit',
          minute: '2-digit',
        }
      : {}),
  })
}

function formatCurrency(amount: number, currencyCode: string) {
  return new Intl.NumberFormat(getAppLocale(), {
    style: 'currency',
    currency: currencyCode,
    maximumFractionDigits: 2,
  }).format(amount)
}

function toTimestamp(value: string | null) {
  if (!value) return Number.NEGATIVE_INFINITY
  const parsed = new Date(value).getTime()
  return Number.isNaN(parsed) ? Number.NEGATIVE_INFINITY : parsed
}

function statusLabel(status: string, t: (key: string) => string) {
  if (status === 'detected') return t('transactions.status.detected')
  if (status === 'pending') return t('transactions.status.pending')
  if (status === 'validated') return t('transactions.status.validated')
  if (status === 'rejected') return t('transactions.status.rejected')
  if (status === 'paid') return t('transactions.status.paid')
  return status
}

function invoiceStatusLabel(status: 'pending' | 'paid' | 'unpaid' | 'overdue' | 'cancelled' | null, t: (key: string) => string) {
  if (!status) return t('transactions.invoiceStatus.none')
  if (status === 'pending') return t('transactions.invoiceStatus.pending')
  if (status === 'paid') return t('transactions.invoiceStatus.paid')
  if (status === 'overdue') return t('transactions.invoiceStatus.overdue')
  if (status === 'cancelled') return t('transactions.invoiceStatus.cancelled')
  return t('transactions.invoiceStatus.unpaid')
}

function transactionStatusBadgeClass(status: string) {
  if (status === 'detected')
    return 'border-transparent bg-blue-500/15 text-blue-800 dark:bg-blue-500/20 dark:text-blue-300'
  if (status === 'pending')
    return 'border-transparent bg-amber-500/15 text-amber-900 dark:bg-amber-500/20 dark:text-amber-300'
  if (status === 'validated')
    return 'border-transparent bg-indigo-500/15 text-indigo-800 dark:bg-indigo-500/20 dark:text-indigo-300'
  if (status === 'rejected')
    return 'border-transparent bg-rose-500/15 text-rose-800 dark:bg-rose-500/20 dark:text-rose-300'
  if (status === 'paid')
    return 'border-transparent bg-emerald-500/15 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-300'
  return 'border-transparent bg-muted text-muted-foreground'
}

const invoiceBadgeClass = {
  pending:
    'border-transparent bg-amber-500/15 text-amber-900 dark:bg-amber-500/20 dark:text-amber-300',
  paid:
    'border-transparent bg-emerald-500/15 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-300',
  unpaid: 'border-transparent bg-muted text-muted-foreground',
  overdue:
    'border-transparent bg-rose-500/15 text-rose-800 dark:bg-rose-500/20 dark:text-rose-300',
  cancelled:
    'border-transparent bg-gray-500/15 text-gray-800 dark:bg-gray-500/20 dark:text-gray-300',
} as const

function syncState(transaction: {
  status: string
  iacrm_transaction_id: string | null
  last_synced_at: string | null
  rejected_at: string | null
}, t: (key: string) => string) {
  if (transaction.status === 'rejected') {
    return {
      label: t('transactions.sync.rejected'),
      className:
        'border-transparent bg-rose-500/15 text-rose-800 dark:bg-rose-500/20 dark:text-rose-300',
    }
  }

  if (transaction.iacrm_transaction_id && transaction.last_synced_at) {
    return {
      label: t('transactions.sync.synced'),
      className:
        'border-transparent bg-emerald-500/15 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-300',
    }
  }

  if (transaction.last_synced_at) {
    return {
      label: t('transactions.sync.localTrace'),
      className:
        'border-transparent bg-blue-500/15 text-blue-800 dark:bg-blue-500/20 dark:text-blue-300',
    }
  }

  return {
    label: t('transactions.sync.pending'),
    className:
      'border-transparent bg-amber-500/15 text-amber-900 dark:bg-amber-500/20 dark:text-amber-300',
  }
}

/* ── Status Stepper ── */

function buildStepperSteps(
  transaction: {
    status: string
    occurred_at: string | null
    recognized_at: string | null
    validated_at: string | null
    rejected_at: string | null
    paid_at: string | null
  },
  t: (key: string) => string,
): WorkflowStep[] {
  const isRejected = transaction.status === 'rejected'

  const steps: WorkflowStep[] = [
    {
      key: 'detected',
      label: t('transactions.detail.audit.occurred.event'),
      icon: ScanLine,
      date: transaction.occurred_at,
      state: 'pending',
    },
    {
      key: 'recognized',
      label: t('transactions.detail.audit.recognized.event'),
      icon: Link2,
      date: transaction.recognized_at,
      state: 'pending',
    },
    {
      key: isRejected ? 'rejected' : 'validated',
      label: isRejected
        ? t('transactions.detail.audit.rejected.event')
        : t('transactions.detail.audit.validated.event'),
      icon: isRejected ? X : Check,
      date: isRejected ? transaction.rejected_at : transaction.validated_at,
      state: 'pending',
    },
    {
      key: 'paid',
      label: t('transactions.detail.audit.paid.event'),
      icon: Banknote,
      date: transaction.paid_at,
      state: 'pending',
    },
  ]

  let activeReached = false
  for (let i = 0; i < steps.length; i++) {
    const step = steps[i]

    if (step.key === 'rejected') {
      step.state = 'error'
      activeReached = true
      continue
    }

    if (step.date) {
      step.state = 'completed'
    } else if (!activeReached) {
      step.state = 'active'
      activeReached = true
    } else {
      step.state = 'pending'
    }
  }

  return steps
}

function StatusStepper({ steps, t }: { steps: WorkflowStep[]; t: (key: string) => string }) {
  return (
    <div className="relative">
      <div className="flex items-start justify-between gap-2">
        {steps.map((step, i) => (
          <div key={step.key} className="relative flex flex-1 flex-col items-center">
            {i > 0 && (
              <div
                className={cn(
                  'absolute right-1/2 top-5 h-0.5 w-full -translate-y-1/2',
                  steps[i - 1].state === 'completed' || steps[i - 1].state === 'active'
                    ? 'bg-emerald-500 dark:bg-emerald-400'
                    : 'bg-border',
                )}
              />
            )}

            <div
              className={cn(
                'relative z-10 flex size-10 items-center justify-center rounded-full text-sm',
                step.state === 'completed' && 'bg-emerald-500 text-white dark:bg-emerald-400',
                step.state === 'active' && 'bg-blue-500 text-white dark:bg-blue-400',
                step.state === 'pending' && 'border-2 border-border bg-background text-muted-foreground',
                step.state === 'error' && 'bg-rose-500 text-white dark:bg-rose-400',
              )}
            >
              {step.state === 'completed' ? (
                <Check className="size-5" />
              ) : step.state === 'error' ? (
                <X className="size-5" />
              ) : (
                <step.icon className="size-5" />
              )}
            </div>

            <p
              className={cn(
                'mt-3 text-center text-sm font-semibold',
                step.state === 'completed' || step.state === 'active'
                  ? 'text-foreground'
                  : step.state === 'error'
                    ? 'text-rose-600 dark:text-rose-400'
                    : 'text-muted-foreground',
              )}
            >
              {step.label}
            </p>

            {step.date ? (
              <p className="mt-1 text-center text-xs text-muted-foreground">
                {formatDate(step.date, true, t)}
              </p>
            ) : (
              <p className="mt-1 text-center text-xs text-muted-foreground/50">—</p>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

/* ── Skeleton ── */

function TransactionDetailSkeleton({ isAgentView }: { isAgentView: boolean }) {
  return (
    <section className="app-section">
      {/* Header */}
      <div className="flex flex-col gap-2 pb-1.5 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
        <div className="flex items-center gap-2">
          <Skeleton className="h-8 w-8 rounded-lg" />
          <Skeleton className="h-6 w-48 rounded-md" />
          <Skeleton className="h-5 w-20 rounded-full" />
          {!isAgentView && <Skeleton className="h-5 w-20 rounded-full" />}
          <Skeleton className="h-5 w-20 rounded-full" />
        </div>
        <div className="flex flex-wrap gap-2">
          <Skeleton className="h-8 w-28 rounded-md" />
        </div>
      </div>

      {/* KPI Cards */}
      <div className={`grid gap-3 ${isAgentView ? 'sm:grid-cols-2' : 'md:grid-cols-2 xl:grid-cols-4'}`}>
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={index} className="rounded-lg bg-card p-4 md:p-6">
            <div className="flex items-center gap-2.5">
              <Skeleton className="size-8 rounded-md" />
              <Skeleton className="h-4 w-24" />
            </div>
            <Skeleton className="mt-4 h-8 w-28" />
            <Skeleton className="mt-4 h-3.5 w-36" />
          </div>
        ))}
      </div>

      {/* Status Stepper */}
      <div className="rounded-lg bg-card app-card-padding">
        <Skeleton className="mb-6 h-5 w-48 rounded-md" />
        <div className="flex items-start justify-between gap-2">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="flex flex-1 flex-col items-center">
              <Skeleton className="size-10 rounded-full" />
              <Skeleton className="mt-3 h-3 w-20" />
              <Skeleton className="mt-1 h-2.5 w-24" />
            </div>
          ))}
        </div>
      </div>

      {/* Sync Table */}
      {!isAgentView && (
        <div className="rounded-lg bg-card app-card-padding">
          <div className="mb-5 flex items-start justify-between">
            <div className="space-y-2">
              <Skeleton className="h-5 w-36 rounded-md" />
              <Skeleton className="h-4 w-48 rounded-md" />
            </div>
            <Skeleton className="h-5 w-16 rounded-full" />
          </div>
          <div className="overflow-hidden rounded-lg border border-border">
            <div className="h-10 bg-muted/30" />
            {Array.from({ length: 4 }).map((_, index) => (
              <div
                key={index}
                className={cn(
                  'flex items-center gap-3 px-4 py-3',
                  index % 2 === 1 && 'bg-muted/15',
                  index > 0 && 'border-t border-border/50',
                )}
              >
                <Skeleton className="h-4 w-28 rounded-md" />
                <Skeleton className="h-4 w-32 rounded-md" />
                <Skeleton className="h-5 w-20 rounded-full" />
                <Skeleton className="ml-auto h-4 w-24 rounded-md" />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Audit Table */}
      {!isAgentView && (
        <div className="rounded-lg bg-card app-card-padding">
          <div className="mb-5 flex items-start justify-between">
            <div className="space-y-2">
              <Skeleton className="h-5 w-32 rounded-md" />
              <Skeleton className="h-4 w-56 rounded-md" />
            </div>
            <Skeleton className="h-5 w-16 rounded-full" />
          </div>
          <div className="overflow-hidden rounded-lg border border-border">
            <div className="h-10 bg-muted/30" />
            {Array.from({ length: 4 }).map((_, index) => (
              <div
                key={index}
                className={cn(
                  'flex items-center gap-3 px-4 py-3',
                  index % 2 === 1 && 'bg-muted/15',
                  index > 0 && 'border-t border-border/50',
                )}
              >
                <Skeleton className="h-4 w-28 rounded-md" />
                <Skeleton className="h-4 w-32 rounded-md" />
                <Skeleton className="h-4 w-28 rounded-md" />
                <Skeleton className="h-4 w-40 rounded-md" />
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  )
}

/* ── Main Page ── */

export function TransactionDetailPage() {
  const { t } = useTranslation()
  const { user } = useAuthSession()
  const isAgentView = Boolean(user?.agent_profile)
  const { transactionId } = useParams<{ transactionId: string }>()
  const [syncSortDirection, setSyncSortDirection] = useState<SortDirection>('desc')
  const [historySortDirection, setHistorySortDirection] = useState<SortDirection>('desc')
  const [syncSortKey, setSyncSortKey] = useState<SyncSortKey>('updatedAt')
  const [historySortKey, setHistorySortKey] = useState<AuditSortKey>('occurredAt')

  const transactionQuery = useQuery({
    queryKey: ['transactions', 'detail', transactionId],
    queryFn: async () => fetchTransaction(transactionId ?? ''),
    enabled: Boolean(transactionId),
  })

  const transaction = transactionQuery.data?.data ?? null

  useAppBreadcrumbTrail(
    transaction
      ? [
          { label: t('transactions.detail.breadcrumb'), to: '/transactions' },
          { label: transaction.transaction_reference },
        ]
      : null,
  )

  const transactionStatusClass = transaction
    ? transactionStatusBadgeClass(transaction.status)
    : transactionStatusBadgeClass('pending')
  const invoiceClass =
    transaction && transaction.invoice_status
      ? invoiceBadgeClass[transaction.invoice_status]
      : 'border-transparent bg-muted text-muted-foreground'
  const sync = transaction
    ? syncState(transaction, t)
    : {
        label: t('transactions.sync.pending'),
        className:
          'border-transparent bg-amber-500/15 text-amber-900 dark:bg-amber-500/20 dark:text-amber-300',
      }

  const workflowSteps = useMemo<WorkflowStep[]>(() => {
    if (!transaction) return []
    return buildStepperSteps(transaction, t)
  }, [transaction, t])

  const syncRows = useMemo(() => {
    if (!transaction) return []

    return [
      {
        key: 'status',
        label: t('transactions.detail.sync.status'),
        value: statusLabel(transaction.status, t),
        badge: <Badge className={transactionStatusClass}>{statusLabel(transaction.status, t)}</Badge>,
        updatedAt: transaction.rejected_at ?? transaction.validated_at ?? transaction.occurred_at,
      },
      {
        key: 'invoice',
        label: t('transactions.detail.sync.invoice'),
        value: invoiceStatusLabel(transaction.invoice_status, t),
        badge: <Badge className={invoiceClass}>{invoiceStatusLabel(transaction.invoice_status, t)}</Badge>,
        updatedAt: transaction.paid_at ?? transaction.validated_at ?? null,
      },
      {
        key: 'sync',
        label: t('transactions.detail.sync.iacrm'),
        value: transaction.iacrm_transaction_id ?? t('transactions.detail.sync.noReference'),
        badge: <Badge className={sync.className}>{sync.label}</Badge>,
        updatedAt: transaction.last_synced_at,
      },
      {
        key: 'recognition',
        label: t('transactions.detail.sync.recognition'),
        value: transaction.recognized_at
          ? t('transactions.detail.sync.recognized')
          : t('transactions.detail.sync.awaitingRecognition'),
        badge: (
          <Badge
            className={
              transaction.recognized_at
                ? 'border-transparent bg-emerald-500/15 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-300'
                : 'border-transparent bg-amber-500/15 text-amber-900 dark:bg-amber-500/20 dark:text-amber-300'
            }
          >
            {transaction.recognized_at ? t('transactions.detail.sync.recognizedBadge') : t('transactions.detail.sync.awaitingBadge')}
          </Badge>
        ),
        updatedAt: transaction.recognized_at,
      },
    ]
  }, [invoiceClass, sync.className, sync.label, t, transaction, transactionStatusClass])

  const auditRows = useMemo(() => {
    if (!transaction) return []

    return [
      {
        key: 'occurred',
        event: t('transactions.detail.audit.occurred.event'),
        system: t('transactions.detail.audit.occurred.system'),
        detail: t('transactions.detail.audit.occurred.detail', { reference: transaction.transaction_reference }),
        occurredAt: transaction.occurred_at,
      },
      transaction.recognized_at
        ? {
            key: 'recognized',
            event: t('transactions.detail.audit.recognized.event'),
            system: t('transactions.detail.audit.recognized.system'),
            detail: t('transactions.detail.audit.recognized.detail'),
            occurredAt: transaction.recognized_at,
          }
        : null,
      transaction.validated_at
        ? {
            key: 'validated',
            event: t('transactions.detail.audit.validated.event'),
            system: t('transactions.detail.audit.validated.system'),
            detail: t('transactions.detail.audit.validated.detail'),
            occurredAt: transaction.validated_at,
          }
        : null,
      transaction.rejected_at
        ? {
            key: 'rejected',
            event: t('transactions.detail.audit.rejected.event'),
            system: t('transactions.detail.audit.rejected.system'),
            detail: t('transactions.detail.audit.rejected.detail'),
            occurredAt: transaction.rejected_at,
          }
        : null,
      transaction.paid_at
        ? {
            key: 'paid',
            event: t('transactions.detail.audit.paid.event'),
            system: t('transactions.detail.audit.paid.system'),
            detail: t('transactions.detail.audit.paid.detail'),
            occurredAt: transaction.paid_at,
          }
        : null,
      transaction.last_synced_at
        ? {
            key: 'synced',
            event: t('transactions.detail.audit.synced.event'),
            system: t('transactions.detail.audit.synced.system'),
            detail: transaction.iacrm_transaction_id
              ? t('transactions.detail.audit.synced.detailWithRef', { ref: transaction.iacrm_transaction_id })
              : t('transactions.detail.audit.synced.detailNoRef'),
            occurredAt: transaction.last_synced_at,
          }
        : null,
    ].filter(Boolean) as Array<{
      key: string
      event: string
      system: string
      detail: string
      occurredAt: string | null
    }>
  }, [t, transaction])

  const sortedSyncRows = useMemo(() => {
    return [...syncRows].sort((left, right) => {
      const result = toTimestamp(left.updatedAt) - toTimestamp(right.updatedAt)
      return syncSortDirection === 'asc' ? result : -result
    })
  }, [syncRows, syncSortDirection])

  const sortedAuditRows = useMemo(() => {
    return [...auditRows].sort((left, right) => {
      const result = toTimestamp(left.occurredAt) - toTimestamp(right.occurredAt)
      return historySortDirection === 'asc' ? result : -result
    })
  }, [auditRows, historySortDirection])

  function handleSyncSort(nextKey: SyncSortKey) {
    if (syncSortKey === nextKey) {
      setSyncSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'))
      return
    }
    setSyncSortKey(nextKey)
    setSyncSortDirection('desc')
  }

  function handleHistorySort(nextKey: AuditSortKey) {
    if (historySortKey === nextKey) {
      setHistorySortDirection((current) => (current === 'asc' ? 'desc' : 'asc'))
      return
    }
    setHistorySortKey(nextKey)
    setHistorySortDirection('desc')
  }

  if (!transactionId) {
    return (
      <article className="rounded-xl border border-red-200 bg-red-50 p-6 text-sm text-red-700">
        {t('transactions.detail.error.missingId')}
      </article>
    )
  }

  if (transactionQuery.isPending) {
    return <TransactionDetailSkeleton isAgentView={isAgentView} />
  }

  if (transactionQuery.isError) {
    return (
      <article className="rounded-xl border border-red-200 bg-red-50 p-6 text-sm text-red-700">
        {(transactionQuery.error as ApiError).message}
      </article>
    )
  }

  if (!transaction) {
    return (
      <article className="rounded-xl border border-red-200 bg-red-50 p-6 text-sm text-red-700">
        {t('transactions.detail.error.notFound')}
      </article>
    )
  }

  return (
    <section className="app-section">
      {/* ── Header ── */}
      <PageHeader
        beforeTitle={
          <Button asChild variant="ghost" size="icon-sm" className="shrink-0">
            <Link to="/transactions" aria-label={t('transactions.detail.back')}>
              <ArrowLeft className="size-4" />
            </Link>
          </Button>
        }
        title={transaction.transaction_reference}
        titleAddon={
          <>
            <Badge className={transactionStatusClass}>{statusLabel(transaction.status, t)}</Badge>
            {!isAgentView && <Badge className={sync.className}>{sync.label}</Badge>}
            {transaction.invoice_status ? (
              <Badge className={invoiceClass}>{invoiceStatusLabel(transaction.invoice_status, t)}</Badge>
            ) : null}
          </>
        }
        right={
          <PageHeaderToolbar>
            {transaction.program_id ? (
              <Button asChild variant="outline" size="sm">
                <Link to={`/programs/${transaction.program_id}`}>{t('transactions.detail.viewProgram')}</Link>
              </Button>
            ) : null}
          </PageHeaderToolbar>
        }
      />

      {/* ── KPI Cards (role-based) ── */}
      <div className={`grid gap-3 ${isAgentView ? 'sm:grid-cols-2' : 'md:grid-cols-2 xl:grid-cols-4'}`}>
        {!isAgentView ? (
          <KpiCard
            title={t('transactions.detail.kpi.amount.title')}
            value={formatCurrency(transaction.amount, transaction.currency_code)}
            description={t('transactions.detail.kpi.amount.description')}
            icon={Banknote}
            tone="success"
          />
        ) : (
          <KpiCard
            title={t('transactions.detail.kpi.points.title')}
            value={
              transaction.points_awarded === null
                ? t('transactions.detail.kpi.points.pending')
                : `${transaction.points_awarded.toLocaleString(getAppLocale())} pts`
            }
            description={t('transactions.detail.kpi.points.description')}
            icon={CircleDollarSign}
            tone="primary"
          />
        )}
        <KpiCard
          title={isAgentView ? t('transactions.table.status') : t('transactions.detail.kpi.points.title')}
          value={
            isAgentView
              ? statusLabel(transaction.status, t)
              : transaction.points_awarded === null
                ? t('transactions.detail.kpi.points.pending')
                : `${transaction.points_awarded.toLocaleString(getAppLocale())} pts`
          }
          description={
            isAgentView
              ? t('transactions.detail.kpi.status.description')
              : t('transactions.detail.kpi.points.description')
          }
          icon={isAgentView ? ShieldCheck : CircleDollarSign}
          tone={isAgentView ? 'info' : 'primary'}
        />
        <KpiCard
          title={isAgentView ? t('transactions.table.program') : t('transactions.table.status')}
          value={
            isAgentView
              ? transaction.program_name ?? t('transactions.fallback.noProgram')
              : statusLabel(transaction.status, t)
          }
          description={
            isAgentView
              ? t('transactions.detail.kpi.program.description')
              : t('transactions.detail.kpi.status.description')
          }
          icon={isAgentView ? Package : ShieldCheck}
          tone={isAgentView ? 'success' : 'warning'}
        />
        <KpiCard
          title={t('transactions.detail.kpi.occurred.title')}
          value={formatDate(transaction.occurred_at, true, t)}
          description={t('transactions.detail.kpi.occurred.description')}
          icon={CalendarClock}
          tone="warning"
        />
      </div>

      {/* ── Status Stepper ── */}
      <div className="rounded-lg bg-card app-card-padding">
        <div className="mb-6">
          <h3 className="text-base font-semibold text-foreground">{t('transactions.detail.timeline.title')}</h3>
          <p className="mt-1 text-sm text-muted-foreground">{t('transactions.detail.timeline.description')}</p>
        </div>
        <StatusStepper steps={workflowSteps} t={t} />
      </div>

      {/* ── Sync State Table (owner only) ── */}
      {!isAgentView && (
        <Card className="border-0 shadow-none">
        <CardHeader className="pb-0">
          <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <CardTitle>{t('transactions.detail.sync.title')}</CardTitle>
              <CardDescription>{t('transactions.detail.sync.description')}</CardDescription>
            </div>
            <Badge variant="secondary">{t('transactions.detail.sync.markers', { count: syncRows.length })}</Badge>
          </div>
        </CardHeader>
        <CardContent className="pt-5">
          <div className="overflow-hidden rounded-lg border border-border">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/30 hover:bg-muted/30">
                  <TableHead className="text-xs font-semibold">{t('transactions.detail.sync.table.element')}</TableHead>
                  <TableHead className="text-xs font-semibold">{t('transactions.detail.sync.table.value')}</TableHead>
                  <TableHead className="text-xs font-semibold">{t('transactions.detail.sync.table.status')}</TableHead>
                  <SortableTableHead
                    sortKey="updatedAt"
                    activeKey={syncSortKey}
                    direction={syncSortDirection}
                    onSort={handleSyncSort}
                    className="text-right text-xs font-semibold"
                    align="right"
                  >
                    {t('transactions.detail.sync.table.lastUpdate')}
                  </SortableTableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedSyncRows.map((row) => (
                  <TableRow key={row.key}>
                    <TableCell className="font-medium text-foreground">{row.label}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{row.value}</TableCell>
                    <TableCell>{row.badge}</TableCell>
                    <TableCell className="text-right text-sm tabular-nums text-muted-foreground">
                      {formatDate(row.updatedAt, true, t)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
      )}

      {/* ── Audit History Table (owner only) ── */}
      {!isAgentView && (
        <Card className="border-0 shadow-none">
        <CardHeader className="pb-0">
          <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <CardTitle>{t('transactions.detail.audit.title')}</CardTitle>
              <CardDescription>{t('transactions.detail.audit.description')}</CardDescription>
            </div>
            <Badge variant="secondary">{t('transactions.detail.audit.events', { count: auditRows.length })}</Badge>
          </div>
        </CardHeader>
        <CardContent className="pt-5">
          {sortedAuditRows.length === 0 ? (
            <div className="rounded-lg bg-muted/20 py-8 text-center text-sm text-muted-foreground">
              {t('transactions.detail.audit.empty')}
            </div>
          ) : (
            <div className="overflow-hidden rounded-lg border border-border">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/30 hover:bg-muted/30">
                    <SortableTableHead
                      sortKey="occurredAt"
                      activeKey={historySortKey}
                      direction={historySortDirection}
                      onSort={handleHistorySort}
                      className="text-xs font-semibold"
                    >
                      {t('transactions.detail.audit.table.date')}
                    </SortableTableHead>
                    <TableHead className="text-xs font-semibold">{t('transactions.detail.audit.table.step')}</TableHead>
                    <TableHead className="text-xs font-semibold">{t('transactions.detail.audit.table.system')}</TableHead>
                    <TableHead className="text-xs font-semibold">{t('transactions.detail.audit.table.detail')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedAuditRows.map((row, index) => (
                    <TableRow key={row.key} className={cn(index % 2 === 1 && 'bg-muted/15')}>
                      <TableCell className="whitespace-nowrap text-sm tabular-nums text-muted-foreground">
                        {formatDate(row.occurredAt, true, t)}
                      </TableCell>
                      <TableCell className="font-medium text-foreground">{row.event}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{row.system}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{row.detail}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
      )}
    </section>
  )
}
